import { useState, useEffect } from "react";
import { DoorOpen, Building2, AlertCircle, Gavel, Loader2, CheckCircle2 } from "lucide-react";
import Modal from "../components/Modal";
import Field, { inputStyle, selectStyle, inputErrorStyle } from "../components/Field";
import { SISTEMAS, BRAZILIAN_STATES } from "../constants";
import { ORIGENS, camposDoSistema, pendenciasDaAdesao, aplicarAdesao, tarefasDeAtivacao } from "../lib/adesao";
import { agendar } from "../lib/followup";
import { generateId, getToday, maskCNPJ, maskPhone, fetchCNPJ } from "../utils";

// ═══════════════════════════════════════════════════════════════
// ADESÃO DO CLIENTE — onde o cliente nasce
// ═══════════════════════════════════════════════════════════════
//
// Vale para os quatro caminhos: prospecção comercial, licitação ganha,
// indicação e cliente trazido por parceiro da rede. Todos nascem aqui, e daqui
// vão para os módulos que contrataram.
//
// A tela pergunta o MÍNIMO que cada módulo precisa para criar o cliente lá
// dentro. Plano, preço, fatura e veículo não entram: são do módulo, com regra
// própria, e uma segunda versão dessas contas aqui seria uma segunda verdade
// sobre dinheiro.

const vazio = () => ({
  company: "", cnpj: "", razaoSocial: "", contact: "", title: "",
  phone: "", email: "", cidade: "", estado: "", address: "",
  origem: "", systems: [], adesao: {}, licitacao: {}, notes: "",
});

export default function AdesaoCliente({ open, cliente, onClose, setClients, setTasks, userId, userName = "" }) {
  const [d, setD] = useState(vazio());
  const [erros, setErros] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [salvo, setSalvo] = useState(false);

  useEffect(() => {
    if (!open) return;
    setErros([]); setSalvo(false);
    setD(cliente
      ? {
        ...vazio(),
        ...cliente,
        systems: cliente.systems || [],
        adesao: cliente.adesao || {},
        licitacao: cliente.licitacao || {},
      }
      : vazio());
  }, [open, cliente]);

  const set = (patch) => setD((p) => ({ ...p, ...patch }));
  const setModulo = (sis, campo, valor) =>
    setD((p) => ({ ...p, adesao: { ...p.adesao, [sis]: { ...(p.adesao?.[sis] || {}), [campo]: valor } } }));

  const buscarCNPJ = async (valor) => {
    const mascarado = maskCNPJ(valor);
    set({ cnpj: mascarado });
    const digitos = mascarado.replace(/\D/g, "");
    if (digitos.length !== 14) return;
    setBuscando(true);
    const r = await fetchCNPJ(digitos);
    setBuscando(false);
    if (!r) return;
    set({
      company: d.company || r.nomeFantasia || r.razaoSocial,
      razaoSocial: r.razaoSocial || d.razaoSocial,
      cidade: r.cidade || d.cidade,
      estado: r.uf || d.estado,
      address: r.enderecoCompleto || d.address,
      email: d.email || r.email || "",
      phone: d.phone || r.telefone || "",
    });
  };

  const alternarSistema = (id) =>
    setD((p) => ({ ...p, systems: p.systems.includes(id) ? p.systems.filter((x) => x !== id) : [...p.systems, id] }));

  const salvar = () => {
    const faltam = pendenciasDaAdesao(d);
    setErros(faltam);
    if (faltam.length) return;

    const base = cliente || {
      id: generateId(), userId, createdAt: getToday(),
      interactions: [], stageHistory: [], historico: [],
    };
    const registro = aplicarAdesao(base, d, { autor: userName });

    setClients((prev) => (prev.some((c) => c.id === registro.id)
      ? prev.map((c) => (c.id === registro.id ? registro : c))
      : [...prev, registro]));

    // Uma tarefa por módulo: a ativação é o que transforma contrato em receita,
    // e sem lembrete ela fica esperando alguém lembrar.
    if (setTasks) {
      for (const t of tarefasDeAtivacao(registro, { userId })) {
        setTasks((prev) => agendar(prev, {
          refId: registro.id, tipo: "client", titulo: t.titulo,
          quando: getToday(), notas: t.notas, userId, prioridade: "high",
        }));
      }
    }

    setSalvo(true);
    setTimeout(() => { setSalvo(false); onClose(); }, 900);
  };

  const origemSel = ORIGENS.find((o) => o.id === d.origem);

  return (
    <Modal open={open} onClose={onClose} title={cliente ? `Adesão — ${cliente.company}` : "Nova adesão de cliente"} w={620}>
      <div style={{ display: "flex", gap: 9, padding: "11px 13px", borderRadius: 9, background: "#f5f3ff", border: "1px solid #ddd6fe", marginBottom: 14 }}>
        <DoorOpen size={16} color="#6d28d9" style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: 11.5, color: "#5b21b6", lineHeight: 1.5 }}>
          O cliente nasce aqui e vai para os módulos que contratou. <strong>Plano, preço e fatura não são preenchidos
          aqui</strong> — cada módulo tem a regra dele, e é lá que a cobrança acontece.
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="CNPJ" required help={buscando ? "Buscando na Receita..." : "Os dados vêm da Receita Federal"}>
            <input style={inputStyle} value={d.cnpj} onChange={(e) => buscarCNPJ(e.target.value)} placeholder="00.000.000/0000-00" />
          </Field>
        </div>
        <div style={{ gridColumn: "1/-1" }}>
          <Field label="Empresa" required>
            <input style={inputStyle} value={d.company} onChange={(e) => set({ company: e.target.value })} placeholder="Nome que a equipe usa" />
          </Field>
        </div>
        <Field label="Responsável" required>
          <input style={inputStyle} value={d.contact} onChange={(e) => set({ contact: e.target.value })} />
        </Field>
        <Field label="Cargo">
          <input style={inputStyle} value={d.title} onChange={(e) => set({ title: e.target.value })} placeholder="Ex: Gestor de frota" />
        </Field>
        <Field label="Telefone">
          <input style={inputStyle} value={d.phone} onChange={(e) => set({ phone: maskPhone(e.target.value) })} />
        </Field>
        <Field label="E-mail">
          <input style={inputStyle} value={d.email} onChange={(e) => set({ email: e.target.value })} />
        </Field>
        <Field label="Cidade">
          <input style={inputStyle} value={d.cidade} onChange={(e) => set({ cidade: e.target.value })} />
        </Field>
        <Field label="Estado (UF)">
          <select style={selectStyle} value={d.estado} onChange={(e) => set({ estado: e.target.value })}>
            <option value="">—</option>
            {BRAZILIAN_STATES.map((s) => <option key={s.uf} value={s.uf}>{s.uf}</option>)}
          </select>
        </Field>
      </div>

      {/* Como chegou — a licitação é caminho de primeira classe, não observação */}
      <div style={{ marginTop: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 7 }}>Como este cliente chegou? *</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 6 }}>
          {ORIGENS.map((o) => (
            <button
              key={o.id} onClick={() => set({ origem: o.id })} title={o.dica}
              style={{
                padding: "9px 11px", borderRadius: 8, textAlign: "left", cursor: "pointer",
                border: `1px solid ${d.origem === o.id ? o.cor : "#e2e8f0"}`,
                background: d.origem === o.id ? `${o.cor}12` : "#fff",
                color: d.origem === o.id ? o.cor : "#475569",
                fontSize: 11.5, fontWeight: d.origem === o.id ? 700 : 600,
              }}
            >
              {o.id === "licitacao" && <Gavel size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />}
              {o.label}
            </button>
          ))}
        </div>
        {origemSel && <div style={{ fontSize: 10.5, color: "#94a3b8", marginTop: 5 }}>{origemSel.dica}</div>}
      </div>

      {d.origem === "licitacao" && (
        <div style={{ marginTop: 10, padding: "12px 14px", borderRadius: 9, background: "#f0fdfa", border: "1px solid #99f6e4" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
            <div style={{ gridColumn: "1/-1" }}>
              <Field label="Órgão / Prefeitura" required>
                <input style={inputStyle} value={d.licitacao?.orgao || ""} onChange={(e) => set({ licitacao: { ...d.licitacao, orgao: e.target.value } })} />
              </Field>
            </div>
            <Field label="Número do processo" required>
              <input style={inputStyle} value={d.licitacao?.numero || ""} onChange={(e) => set({ licitacao: { ...d.licitacao, numero: e.target.value } })} />
            </Field>
            <Field label="Vigência do contrato">
              <input style={inputStyle} value={d.licitacao?.vigencia || ""} onChange={(e) => set({ licitacao: { ...d.licitacao, vigencia: e.target.value } })} placeholder="Ex: 12 meses" />
            </Field>
          </div>
        </div>
      )}

      {/* Módulos contratados */}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 7 }}>O que ele contratou? *</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 6 }}>
          {SISTEMAS.map((s) => {
            const on = d.systems.includes(s.id);
            return (
              <button
                key={s.id} onClick={() => alternarSistema(s.id)}
                style={{
                  display: "flex", alignItems: "center", gap: 7, padding: "10px 12px", borderRadius: 8, cursor: "pointer",
                  border: `1px solid ${on ? s.color : "#e2e8f0"}`, background: on ? `${s.color}12` : "#fff",
                  color: on ? s.color : "#475569", fontSize: 12, fontWeight: on ? 700 : 600,
                }}
              >
                {on ? <CheckCircle2 size={14} /> : <Building2 size={14} />} {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* O mínimo que cada módulo precisa */}
      {d.systems.map((sis) => {
        const s = SISTEMAS.find((x) => x.id === sis);
        const campos = camposDoSistema(sis);
        if (!campos.length) return null;
        return (
          <div key={sis} style={{ marginTop: 10, padding: "12px 14px", borderRadius: 9, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: s?.color || "#475569", marginBottom: 8 }}>{s?.label || sis}</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
              {campos.map((c) => (
                <Field key={c.campo} label={c.label + (c.opcional ? "" : " *")} help={c.dica}>
                  <input
                    style={inputStyle}
                    type={c.tipo === "numero" ? "number" : "text"}
                    value={d.adesao?.[sis]?.[c.campo] || ""}
                    onChange={(e) => setModulo(sis, c.campo, e.target.value)}
                  />
                </Field>
              ))}
            </div>
          </div>
        );
      })}

      <div style={{ marginTop: 12 }}>
        <Field label="Observações da adesão" help="O que foi combinado e não cabe nos campos acima">
          <textarea style={{ ...inputStyle, minHeight: 54, resize: "vertical" }} value={d.notes} onChange={(e) => set({ notes: e.target.value })} />
        </Field>
      </div>

      {erros.length > 0 && (
        <div style={{ marginTop: 12, padding: "10px 12px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11.5, fontWeight: 700, color: "#991b1b", marginBottom: 4 }}>
            <AlertCircle size={12} /> Falta preencher
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: "#991b1b", lineHeight: 1.6 }}>
            {erros.map((e) => <li key={e}>{e}</li>)}
          </ul>
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 14, borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
        <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 7, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          Cancelar
        </button>
        <button onClick={salvar} disabled={salvo} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 18px", borderRadius: 7, border: "none", background: salvo ? "#059669" : "#7c3aed", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
          {salvo ? <><CheckCircle2 size={13} /> Registrada</> : buscando ? <><Loader2 size={13} className="girar" /> Buscando...</> : "Registrar adesão"}
        </button>
      </div>
      <style>{`.girar { animation: girar 1s linear infinite } @keyframes girar { to { transform: rotate(360deg) } }`}</style>
    </Modal>
  );
}
